import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Footer({ onNavigate }) {
  const { t } = useTranslation();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    setIsSubscribed(localStorage.getItem('newsletter_subscribed') === 'true');
    const interval = setInterval(() => {
      setIsSubscribed(localStorage.getItem('newsletter_subscribed') === 'true');
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <footer
      style={{
        backgroundColor: '#FAF6F0',
        borderTop: '1px solid rgba(215, 215, 215, 1)',
        padding: '20px 0 10px',
        position: 'relative'
      }}
    >
      <div
        style={{
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}
      >
        {/* Top: Brand and details */}
        <div
          className="footer-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}
        >
          {/* Logo and Tagline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src="/images/logo.png" alt="MysticRoutes Spiritual Tourism Platform" style={{ height: '45px', objectFit: 'contain' }} />
            </Link>

            <p className="font-serif" style={{ fontStyle: 'italic', fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.5', margin: 0 }}>
              {t('footer.quote')}
            </p>

            {/* Social Icons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '5px' }}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="social-icon" aria-label="Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
              </a>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="social-icon" aria-label="Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
              </a>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="social-icon" aria-label="YouTube">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-serif text-gold" style={{ fontSize: '1.1rem', marginBottom: '12px', letterSpacing: '0.05em' }}>
              {t('footer.allPages', { defaultValue: 'All Pages' })}
            </h4>
            <ul style={{ listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: 'var(--text-muted)', padding: 0, margin: 0 }}>
              <li><Link to="/" className="footer-link">{t('header.navHome', { defaultValue: 'Home' })}</Link></li>
              <li><Link to="/sanctuary" className="footer-link">{t('header.navSanctuary', { defaultValue: 'Sanctuaries' })}</Link></li>
              <li><Link to="/experiences" className="footer-link">{t('header.navExperiences', { defaultValue: 'Experiences' })}</Link></li>
              <li><Link to="/planner" className="footer-link">{t('header.navPlanner', { defaultValue: 'Journey Planner' })}</Link></li>
              <li><Link to="/retreats" className="footer-link">{t('header.navRetreats', { defaultValue: 'Retreats & Bookings' })}</Link></li>
              <li><Link to="/guide" className="footer-link">{t('header.navConsult', { defaultValue: 'Darshan' })}</Link></li>
              <li><Link to="/stories" className="footer-link">{t('header.navStories', { defaultValue: 'Seeker Stories' })}</Link></li>
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h4 className="font-serif text-gold" style={{ fontSize: '1.1rem', marginBottom: '12px', letterSpacing: '0.05em' }}>
              {t('footer.consultancy')}
            </h4>
            <ul style={{ listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.8rem', color: 'var(--text-muted)', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <MapPin size={14} className="text-saffron" style={{ marginTop: '3px', flexShrink: 0 }} />
                <a
                  href="https://www.google.com/maps/place/LOTUS+PARADISE+PLAZA/@12.8738583,74.8346968,14z/data=!3m1!5s0x3ba35a372ebaf0c1:0xaeec3cf57ddf4c36!4m10!1m2!2m1!1s2nd+Floor,+Lotus+Paradise+Plaza,+Bendorwell,+Bendoor,+Mangaluru,+Karnataka+575002!3m6!1s0x3ba35b4e6c671b49:0x6b2130290133066f!8m2!3d12.874685!4d74.8561628!15sClIybmQgRmxvb3IsIExvdHVzIFBhcmFkaXNlIFBsYXphLCBCZW5kb29yd2VsbCwgQmVuZG9vciwgTWFuZ2FsdXJ1LCBLYXJuYXRha2EgNTc1MDAyWk8iTTJuZCBmbG9vciBsb3R1cyBwYXJhZGlzZSBwbGF6YSBiZW5kb29yd2VsbCBiZW5kb29yIG1hbmdhbHVydSBrYXJuYXRha2EgNTc1MDAykgEPc2hvcHBpbmdfY2VudGVymgFEQ2k5RFFVbFJRVU52WkVOb2RIbGpSamx2VDI1b2FVNVhaRFpWTVVVeFkyeFNRMUZ1VVhkaFIxa3dWa1ZzVEZVeVl4QULgAQD6AQQIOBAT!16s%2Fg%2F11j7ygf3wc?entry=ttu&g_ep=EgoyMDI2MDUyNi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                  style={{ textDecoration: 'none', color: 'inherit', lineHeight: '1.4' }}
                >
                  2nd Floor, Lotus Paradise Plaza,<br />
                  Bendorwell, Bendoor, Mangaluru,<br />
                  Karnataka 575002
                </a>
              </li>
              <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Phone size={14} className="text-saffron" />
                <span>+91 135 243 0088</span>
              </li>
              <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Mail size={14} className="text-saffron" />
                <span>seek@bharatspiritual.com</span>
              </li>
            </ul>
          </div>

          {/* Map Location */}
          <div>
            <h4 className="font-serif text-gold" style={{ fontSize: '1.1rem', marginBottom: '12px', letterSpacing: '0.05em' }}>
              Our Location
            </h4>
            <div style={{ borderRadius: '8px', overflow: 'hidden', height: '120px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <iframe
                title="Google Maps - Lotus Paradise Plaza"
                src="https://maps.google.com/maps?q=Lotus+Paradise+Plaza,+Mangaluru&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Bottom: Legal copy */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.05)',
            paddingTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px',
            fontSize: '0.75rem',
            color: 'var(--text-muted)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>&copy; 2026 SUNSHINE TRAVELS. ALL RIGHTS RESERVED.</span>
            <span>POWERED BY <a href="https://datavex.ai/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-gold)', textDecoration: 'none' }}>DATAVEX.AI</a></span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: '500',
            color: isSubscribed ? '#ef4444' : 'var(--text-muted)',
            background: isSubscribed ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.05)',
            padding: '4px 12px',
            borderRadius: '20px',
            border: isSubscribed ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: isSubscribed ? '0 0 10px rgba(239, 68, 68, 0.1)' : 'none'
          }}>
            {isSubscribed ? (
              <>
                <span>●</span>
                <span>Newsletter Subscribed</span>
              </>
            ) : (
              <>
                <span>○</span>
                <span>Not Subscribed</span>
              </>
            )}
          </div>

          <div className="footer-links" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <Link to="/admin" className="footer-link" style={{ opacity: 0.65 }}>Portal Access</Link>
            <Link to="/terms" className="footer-link">Terms and Service</Link>
            <Link to="/privacy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .footer-links { flex-wrap: wrap !important; justify-content: center !important; gap: 12px !important; }
        }
        .social-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(212, 175, 55, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          transition: var(--transition-fast);
        }
        .social-icon:hover {
          color: var(--primary-gold);
          border-color: var(--primary-gold);
          background: var(--primary-gold-trans);
          transform: translateY(-2px);
        }
        .footer-link {
          background: none;
          border: none;
          padding: 0;
          font-family: inherit;
          font-size: inherit;
          color: inherit;
          cursor: pointer;
          transition: var(--transition-fast);
        }
        .footer-link:hover {
          color: var(--primary-gold);
          text-decoration: underline;
        }
      `}</style>
    </footer>
  );
}
